import { zodResolver } from "@hookform/resolvers/zod";
import { CircleCheck, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useJoinWaitlist } from "../../hooks/use-join-waitlist";
import {
	type WaitlistFormValues,
	waitlistSchema,
} from "../../schemas/waitlist-schema";

interface WaitlistViolations {
	name?: string;
	email?: string;
}

function scopeWaitlistViolations(
	violations: Record<string, string>,
): WaitlistViolations {
	const scoped: WaitlistViolations = {};

	for (const [field, message] of Object.entries(violations)) {
		const key = field.split(".")[0];
		if (key === "name" || key === "email") {
			scoped[key] = message;
		}
	}

	return scoped;
}

export function WaitlistStep() {
	const join = useJoinWaitlist();
	const [violations, setViolations] = useState<WaitlistViolations>({});
	const [joinError, setJoinError] = useState<string>();
	const [joinedEmail, setJoinedEmail] = useState<string>();
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<WaitlistFormValues>({
		resolver: zodResolver(waitlistSchema),
		defaultValues: { name: "", email: "" },
	});

	function onSubmit(values: WaitlistFormValues) {
		setViolations({});
		setJoinError(undefined);
		join.mutate(
			{ name: values.name, email: values.email },
			{
				onSuccess: () => setJoinedEmail(values.email),
				onError: (err) => {
					if (err instanceof APIError && err.code === "validation") {
						setViolations(scopeWaitlistViolations(err.violations));
						return;
					}
					setJoinError(
						"Não foi possível registrar seu cadastro. Tente novamente.",
					);
				},
			},
		);
	}

	if (joinedEmail) {
		return (
			<div className="waitlist-step__success">
				<span className="waitlist-step__success-icon">
					<CircleCheck />
				</span>
				<div className="waitlist-step__success-heading">
					<h1 className="waitlist-step__success-title">Você está na lista!</h1>
					<p className="waitlist-step__success-description">
						Enviamos um e-mail de confirmação para{" "}
						<strong className="waitlist-step__success-email">
							{joinedEmail}
						</strong>
						. Assim que liberarmos o seu acesso, avisamos por lá.
					</p>
				</div>
				<p className="waitlist-step__footer">
					Já tem uma conta?{" "}
					<Link to="/login" className="waitlist-step__login-link">
						Entrar
					</Link>
				</p>
			</div>
		);
	}

	return (
		<form
			noValidate
			className="waitlist-step"
			onSubmit={handleSubmit(onSubmit)}
		>
			<div className="waitlist-step__heading">
				<h1 className="waitlist-step__title">Entre na lista de espera</h1>
				<p className="waitlist-step__subtitle">
					Estamos liberando o acesso aos poucos. Deixe seu contato e avisamos
					assim que chegar a sua vez.
				</p>
			</div>

			{!!joinError && (
				<p role="alert" className="form-error-alert">
					{joinError}
				</p>
			)}

			<div className="form-group">
				<Label htmlFor="name">Nome completo</Label>
				<Input
					id="name"
					autoComplete="name"
					placeholder="Como devemos te chamar?"
					aria-invalid={!!errors.name || !!violations.name}
					{...register("name")}
				/>
				{(errors.name || violations.name) && (
					<p className="form-error">
						{errors.name?.message ?? violations.name}
					</p>
				)}
			</div>

			<div className="form-group">
				<Label htmlFor="email">E-mail</Label>
				<Input
					id="email"
					type="email"
					autoComplete="email"
					placeholder="voce@empresa.com.br"
					aria-invalid={!!errors.email || !!violations.email}
					{...register("email")}
				/>
				{(errors.email || violations.email) && (
					<p className="form-error">
						{errors.email?.message ?? violations.email}
					</p>
				)}
			</div>

			<Button
				type="submit"
				className="waitlist-step__submit"
				disabled={join.isPending}
			>
				{join.isPending && <Loader2 className="auth-spinner" />}
				Entrar na lista
			</Button>

			<p className="waitlist-step__footer">
				Já tem uma conta?{" "}
				<Link to="/login" className="waitlist-step__login-link">
					Entrar
				</Link>
			</p>
		</form>
	);
}
